import { useEffect, useState } from 'react'
import { useLiveQuery } from '@tanstack/react-db'

import { reminderNoteLabel } from '@/db/domain/customerReminders'
import { isReadyForCollection } from '@/db/domain/jobStatus'
import { toZaDateKey, toZaTime } from '@/lib/datetime'
import { useDb } from '@/lib/dbContext'
import { toWhatsAppNumber } from '@/lib/whatsapp'
import {
  RemindersScreen,
  type ReminderRow,
  type RemindersScope,
} from '@/screens/RemindersScreen'

const zaLabel = (iso?: string) =>
  iso ? `${toZaDateKey(iso)} ${toZaTime(iso)}` : ''

/** The Scheduled / Archived tabs: every customer with devices waiting (or once waiting) for collection. */
export function RemindersTab({ scope }: { scope: RemindersScope }) {
  const db = useDb()
  const remindersResult = useLiveQuery((q) =>
    q.from({ reminders: db.customerRemindersCollection }),
  )
  const customersResult = useLiveQuery((q) =>
    q.from({ customers: db.customersCollection }),
  )
  const jobsResult = useLiveQuery((q) => q.from({ jobs: db.jobsCollection }))
  const messagesResult = useLiveQuery((q) =>
    q.from({ messages: db.whatsappMessagesCollection }),
  )
  const [busyId, setBusyId] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    void db.customerRemindersCollection.preload?.()
    void db.whatsappMessagesCollection.preload?.()
  }, [db])

  if (remindersResult.isLoading) {
    return <p className="p-4 text-sm text-slate-600">Loading reminders…</p>
  }
  if (remindersResult.isError) {
    return (
      <p className="p-4 text-sm text-slate-600">
        Unable to load reminders right now.
      </p>
    )
  }

  const customers = new Map(
    (customersResult.data ?? []).map((customer) => [customer.id, customer]),
  )
  const readyJobsByCustomer = new Map<string, NonNullable<typeof jobsResult.data>>()
  for (const job of jobsResult.data ?? []) {
    if (!job.customerId || !isReadyForCollection(job.status)) continue
    readyJobsByCustomer.set(job.customerId, [
      ...(readyJobsByCustomer.get(job.customerId) ?? []),
      job,
    ])
  }

  // What each reminder's messages actually did, from the WhatsApp log.
  const outcomes = new Map<
    string,
    {
      sent: number
      held: number
      queued: number
      cancelled: number
      failed: number
      lastQueuedAt?: string
      lastQueuedStatus?: string
    }
  >()
  for (const message of messagesResult.data ?? []) {
    if (!message.reminderId) continue
    const entry = outcomes.get(message.reminderId) ?? {
      sent: 0,
      held: 0,
      queued: 0,
      cancelled: 0,
      failed: 0,
    }
    // "Queued" = when the reminder put the message on the queue, whatever became of it.
    if (!entry.lastQueuedAt || message.createdAt > entry.lastQueuedAt) {
      entry.lastQueuedAt = message.createdAt
      entry.lastQueuedStatus = message.status
    }
    if (message.status === 'Sent') {
      entry.sent += 1
    } else if (message.status === 'Held') entry.held += 1
    else if (message.status === 'Queued') entry.queued += 1
    else if (message.status === 'Cancelled') entry.cancelled += 1
    else if (message.status === 'Failed') entry.failed += 1
    outcomes.set(message.reminderId, entry)
  }

  const rows: ReminderRow[] = (remindersResult.data ?? [])
    .filter((reminder) =>
      scope === 'archived'
        ? reminder.state === 'archived'
        : reminder.state !== 'archived',
    )
    .map((reminder) => {
    const outcome = outcomes.get(reminder.id)
    const customer = customers.get(reminder.customerId)
    const jobs = readyJobsByCustomer.get(reminder.customerId) ?? []
    return {
      id: reminder.id,
      customerId: reminder.customerId,
      customerName: customer?.fullName ?? 'Unknown customer',
      customerPublicId: customer?.publicId,
      phone: toWhatsAppNumber(customer?.contactPhone ?? jobs[0]?.contactPhone ?? ''),
      jobs: jobs.map((job) => ({
        id: job.id,
        publicId: job.publicId,
        deviceType: job.deviceType,
        unsuccessful: job.status === 'ReadyForCollectionUnsuccessful',
      })),
      state: reminder.state,
      noteLabel: reminderNoteLabel(reminder.statusNote),
      messageCounts: {
        sent: outcome?.sent ?? 0,
        held: outcome?.held ?? 0,
        queued: outcome?.queued ?? 0,
        cancelled: outcome?.cancelled ?? 0,
        failed: outcome?.failed ?? 0,
      },
      archivedLabel: zaLabel(reminder.archivedAt),
      lastQueuedLabel: zaLabel(outcome?.lastQueuedAt),
      lastQueuedAt: outcome?.lastQueuedAt,
      lastQueuedStatus: outcome?.lastQueuedStatus,
      nextSendLabel: zaLabel(reminder.nextSendAt),
      // Stopped/expired/unscheduled rows sort after the ones about to send.
      sortTimestamp: reminder.nextSendAt ?? '9999',
    }
  })

  const run = async (id: string, work: (id: string) => Promise<void>) => {
    setBusyId(id)
    setErrorMessage(null)
    try {
      await work(id)
    } catch (error) {
      setErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : 'Could not update the reminder. Please try again.',
      )
    } finally {
      setBusyId(null)
    }
  }

  return (
    <RemindersScreen
      rows={rows}
      scope={scope}
      busyId={busyId}
      errorMessage={errorMessage}
      onStop={(id) => run(id, db.stopCustomerReminder)}
      onResume={(id) => run(id, db.resumeCustomerReminder)}
    />
  )
}
